<template>
  <div class="section bg-main pb-56">
    <BreadCrumbs />
    <div
      class="
        flex
        md:flex-row
        flex-col
        container
        xxl:px-24
        md:px-56
        px-0
        lg:max-h-380
      "
    >
      <div class="flex justify-center flex-1 relative overflow-hidden">
        <nuxt-img
          v-if="
            (uiStore.isMobile && plpDocument.headerImageMobile) ||
              plpDocument.headerImageDesktop
          "
          :src="
            uiStore.isMobile ? plpDocument.headerImageMobile : plpDocument.headerImageDesktop
          "

          preset="standard"
          sizes="mob375:100vw mob390:100vw mob414:100vw desk:655px"
          class="w-full md:h-full h-240 object-cover"
          alt=""
        />
      </div>
      <div
        class="bg-primary flex-1 flex items-center"
        :style="`background-color: ${plpDocument.headerBackgroundColor}`"
      >
        <div
          class="md:px-80 pt-32 pb-24 px-24 h-full flex flex-col justify-center"
        >
          <h1 class="text-preamble-large text-white mb-8">
            {{ plpDocument.headerTitle }}
          </h1>
          <div class="text-base-regular text-white mb-16">{{ plpDocument.headerText }}</div>
          <div class="flex flex-wrap">
            <div
              v-for="link in plpDocument.headerLinks"
              :key="'product-listing-hero-links-' + link.link?.url"
              class="mb-8 mr-8"
            >
              <ButtonComponent
                :is-small-btn="true"
                :is-ghost-btn="true"
                :text="link.link?.title"
                :is-secondary-btn="true"
                :link-url="link.link?.url || '/'"
              />
            </div>
          </div>
          <div class="flex flex-wrap">
            <div
              v-for="link in plpDocument.headerLinksJs"
              :key="'product-listing-hero-links-' + link.title"
              class="mb-8 mr-8"
            >
              <ButtonComponent
                :is-small-btn="true"
                :is-ghost-btn="true"
                :text="link.title"
                :is-secondary-btn="true"
                @click="openJavascriptLink(link.javascript)"
              />
            </div>
          </div>
        </div>
      </div>
    </div>
    <div class="container xxl:px-24 md:px-56 px-0">
      <div class="flex pt-24 md:px-0 pr-24 pl-24 mb-16 justify-between">
        <div class="flex flex-1 flex-wrap">
          <ButtonComponent
            class="md:flex-initial flex-1 md:mr-8 mr-4 md:mb-8"
            :text="$lang('product_listing', 'filter_btn')"
            :icon="'stream'"
            :icon-lib="'far'"
            :is-block="true"
            :is-large-btn="uiStore.isMobile"
            :is-small-btn="!uiStore.isMobile"
            @click="toggleFilterMenu"
          />
          <div v-if="!uiStore.isMobile" class="flex flex-1 flex-wrap">
            <div class="mr-8 md:mb-8">
              <ButtonComponent
                v-if="filterTags.length > 0"
                :is-danger-btn="true"
                :is-small-btn="true"
                :text="$lang('product_listing', 'clear_btn')"
                @click="clearFilters"
              />
            </div>
            <div
              v-for="filterTag in filterTags"
              :key="'product-listing-filter-' + filterTag.tag"
              class="mr-8 md:mb-8"
            >
              <div
                class="
                  rounded-3xl
                  py-8
                  pl-16
                  bg-secondary
                  text-primary text-price
                  flex
                  items-center
                  relative
                "
                :class="{
                  'pr-32': !filterTag.isLocked,
                  'pr-16': filterTag.isLocked,
                }"
              >
                {{ filterTag.tag }}
                <div
                  v-if="!filterTag.isLocked"
                  class="absolute right-16 py-8 pl-8 cursor-pointer select-none"
                  @click="removeFilter(filterTag.key, filterTag.tag)"
                >
                  <fa class="text-sm" :icon="['far', 'times']" />
                </div>
              </div>
            </div>
          </div>
        </div>
        <ButtonComponent
          class="md:flex-initial flex-1 ml-4"
          :text="$lang('product_listing', 'sort_btn')"
          :icon="'sort'"
          :icon-lib="'fas'"
          :is-block="true"
          :is-large-btn="uiStore.isMobile"
          :is-small-btn="!uiStore.isMobile"
          :is-secondary-btn="true"
          @click="toggleSortingMenu"
        />
      </div>
      <!-- swiper here -->
      <div
        v-if="uiStore.isMobile"
        class="swiper-container md:pr-0 ml-0 pl-24 pb-24"
      >
        <div class="swiper-wrapper flex">
          <div class="swiper-slide w-auto mr-8">
            <ButtonComponent
              v-if="filterTags.length > 0"
              :is-danger-btn="true"
              :is-small-btn="true"
              :text="'Rensa'"
              @click="clearFilters"
            />
          </div>
          <div
            v-for="filterTag in filterTags"
            :key="'product-listing-filter-' + filterTag.tag"
            class="swiper-slide w-auto mr-8"
          >
            <div
              class="
                rounded-3xl
                py-8
                pl-16
                pr-32
                bg-secondary
                text-primary text-price
                flex
                items-center
                relative
              "
            >
              {{ filterTag.tag }}
              <div
                class="absolute right-16 py-8 pl-8"
                @click="removeFilter(filterTag.key, filterTag.tag)"
              >
                <fa class="text-sm" :icon="['far', 'times']" />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
    <div class="container xxl:px-24 md:px-56 px-0">
      <div
        class="product-listing flex flex-wrap"
        itemscope
        itemtype="https://schema.org/ItemList"
      >
        <div
          v-for="(batch, batchIndex) in batches"
          :key="'product-batches-' + batchIndex"
          class="flex flex-wrap w-full"
        >
          <template v-for="(item, i) in batch" :key="`${item.ecomId || i}-main-productlist-spot`">
            <ProductSpot 
              v-if="!isSeller || item.brandName==='Ullmax'"
              ref="productSpot"
              itemprop="itemListElement"
              :product="item"
              :index="i"
              :large="uiStore.isMobile && i !== 0 && (i + 1) % 5 === 0 && !hasGridComponents"
              :is-mobile="uiStore.isMobile"
              :is-mostright="(!uiStore.isMobile && (i + 1) % 4 === 0) || (uiStore.isMobile && hasGridComponents && (i + 1) % 2 === 0 ) || (uiStore.isMobile && !hasGridComponents && ((i + 1) % 10 === 2 || (i + 1) % 10 === 4 || (i + 1) % 10 === 7 || (i + 1) % 10 === 9 || (i + 1) % 5 === 0))"
              :language="config.market.language"
              :list-title="plpDocument.title"
            />
            <component
              :is="components[component.layout]"
              v-for="(component, idx) in gridComponents(1 + batchIndex + i)"
              :key="component.layout + idx"
              v-bind="{ component }"
              :in-grid="true"
            />
          </template>
        </div>
      </div>
    </div>
    <div
      v-if="plpDocument.components.length > 0"
      class="container xxl:px-24 pt-56"
    >
      <component
        :is="components[component.layout]"
        v-for="(component, idx) in gridComponents(0)"
        :key="component.layout + idx"
        v-bind="{ component }"
      />
    </div>
    <FilterMenu
      v-if="filterMenuToggled"
      :filters="availableFilters"
      :active-filters="activeFilters"
      :amountofproducts="products.length"
      :locked-filters="page.product_listing.ecom_attribute"
      @on-toggle-filter="toggleFilter"
      @on-filter-menu-close="toggleFilterMenu"
      @clear-filter="clearFilters"
    />
    <SortingMenu
      v-if="sortingMenuToggled"
      :active-option="sortingKey"
      :options="sortingSelections"
      :loading="false"
      @on-sorting-menu-close="toggleSortingMenu"
      @on-sort="(e) => sortingKey = e"
    />
  </div>
</template>

<script setup lang="ts">
import type { Ref } from 'vue';
import ProductListingPage from '~/models/product-listing-page';
import { useUiStore } from '~/store/ui';
import type { EcomProduct } from '~/constants/types/product';
import ButtonComponent from '~/components/forms/ButtonComponent.vue';
import Product from '~/models/product';
import ProductSpot from '~/components/productlist/ProductSpot.vue';
import { useConfigStore } from '~/store/config';
import BlockComponents from '~/constants/block-components';
import BreadCrumbs from '~/components/productpage/BreadCrumbs.vue';
import SortingMenu from '~/components/productlist/SortingMenu.vue';
import dayjs from 'dayjs';
import FilterMenu from '~/components/productlist/FilterMenu.vue';
const { fetchProducts } = useProductService();
const config = useConfigStore();
const { $lang } = useNuxtApp();
const { sellerCookieOptions } = useCookieConfig();
const isSeller = useCookie<boolean>('isSeller', sellerCookieOptions) as Ref<boolean>;

const uiStore = useUiStore();

const props = defineProps<{
  page: any,
}>();

const activeFilters = ref<{ [name: string]: string[] }>(props.page.product_listing.ecom_attribute);
const availableFilters = ref<{ [name: string]: object[] }>({});
const fetchedProducts = ref<EcomProduct[]>([]);
const filterMenuToggled = ref(false);

const plpDocument = computed<ProductListingPage>(()=> {
  return new ProductListingPage({ contentData: props.page });
});

const processFilters = (loadedFilters: any) => {
  const thisAvailableFilters = {} as { [name: string]: object[] };
  loadedFilters.forEach(
    (filter: {
      values: { count: number; field: string; data: object }[];
      field: string;
    }) => {
      if (
        filter.field !== 'pr_washing.name' &&
        filter.field !== 'pr_seal.name' &&
        filter.field !== 'pr_news_value'
      ) {
        filter.values.forEach(
          (value: { count: number; field: string; data: object }) => {
            if (value.count > 0) {
              const iteratedFilter = thisAvailableFilters[filter.field]
                ? thisAvailableFilters[filter.field]
                : [];
              thisAvailableFilters[filter.field] = [
                ...iteratedFilter,
                value.data,
              ];
            }
          }
        );
      }
    });
  availableFilters.value = thisAvailableFilters;
};

const { data } = await useAsyncData('productListLoad', async() => {
  const payload = {
    ...activeFilters.value,
    categories: [...plpDocument.value.ecomCategories],
  };
  const res = await fetchProducts(payload);
  if (res) {
    return res;
  } else {
    console.error();
    uiStore.setError('Couldn\'t find any product listings');
    return null;
  }
});

// Set up products and filtering
if (data) {
  fetchedProducts.value = data.value?.products || [];
  processFilters(data.value?.data?.filter || []);
}

// * * * * * * * * * * * * * * Sorting
const SortingKeys = {
  byNews: 'byNews',
  byDate: 'byDate',
  byPriceDesc: 'byPriceDesc',
  byPriceAsc: 'byPriceAsc',
  byNameDesc: 'byNameDesc',
  byNameAsc: 'byNameAsc',
};

const sortingSelections = [
  {
    key: SortingKeys.byDate,
    label: $lang('product_listing', 'sort_by_date'),
  },
  {
    key: SortingKeys.byNews,
    label: $lang('product_listing', 'sort_by_news'),
  },
  {
    key: SortingKeys.byPriceDesc,
    label: $lang('product_listing', 'sort_by_price_desc'),
  },
  {
    key: SortingKeys.byPriceAsc,
    label: $lang('product_listing', 'sort_by_price_asc'),
  },
  {
    key: SortingKeys.byNameDesc,
    label: $lang('product_listing', 'sort_by_name_desc'),
  },
  {
    key: SortingKeys.byNameAsc,
    label: $lang('product_listing', 'sort_by_name_asc'),
  },
];

const sortingKey = ref('');

const products = computed<Product[]>(()=> {
  const products = fetchedProducts.value.map(
    (p: EcomProduct) => new Product({ contentData: null, ecomData: p })
  );

  let sortedArray;
  if (sortingKey.value === SortingKeys.byNews) {
    sortedArray = products.sort((a, z) => {
      if (a.isNews && !z.isNews) {
        return -1;
      }
      if (!a.isNews && z.isNews) {
        return 1;
      }
      return 0;
    });
    return sortedArray;
  } else if (sortingKey.value === SortingKeys.byDate) {
    sortedArray = products.sort(
      (a, z) => {
        return dayjs(a.createdAt).unix() > dayjs(z.createdAt).unix() ? -1:1;
      }
    );
    return sortedArray;
  } else if (sortingKey.value === SortingKeys.byPriceDesc) {
    sortedArray = products.sort(
      (a, z) => z.priceAsNumber - a.priceAsNumber
    );
    return sortedArray;
  } else if (sortingKey.value === SortingKeys.byPriceAsc) {
    sortedArray = products.sort(
      (a, z) => a.priceAsNumber - z.priceAsNumber
    );
    return sortedArray;
  } else if (sortingKey.value === SortingKeys.byNameDesc) {
    sortedArray = products.sort((a, z) => {
      const aTitle = a.title.toUpperCase();
      const zTitle = z.title.toUpperCase();
      if (aTitle < zTitle) {
        return -1;
      }
      if (aTitle > zTitle) {
        return 1;
      }
      return 0;
    });
    return sortedArray;
  } else if (sortingKey.value === SortingKeys.byNameAsc) {
    sortedArray = products.sort((a, z) => {
      const aTitle = a.title.toUpperCase();
      const zTitle = z.title.toUpperCase();
      if (aTitle > zTitle) {
        return -1;
      }
      if (aTitle < zTitle) {
        return 1;
      }
      return 0;
    });

    return sortedArray;
  } else {
    return products;
  }
});

const sortingMenuToggled = ref(false);
const toggleSortingMenu = () => {
  sortingMenuToggled.value = !sortingMenuToggled.value;
};

// * * * * * * * * * * * * * * Filtering
const lockedFilters = computed(()=> {
  return props.page.product_listing.ecom_attribute;
});

const filterTags = computed<{ key: string; tag: string; isLocked: boolean }[]>(()=> {
  let tags: { key: string; tag: string; isLocked: boolean }[] = [];
  Object.entries(activeFilters.value).forEach((filter) => {
    const key = filter[0];
    const filterTags = filter[1];
    filterTags.forEach((tag) => {
      tags = [
        ...tags,
        {
          key: filter[0],
          tag,
          isLocked:
            lockedFilters.value[key] &&
            lockedFilters.value[key].includes(tag),
        },
      ];
    });
  });
  return tags;
});

const toggleFilterMenu = () => {
  filterMenuToggled.value = !filterMenuToggled.value;
};

const toggleFilter = (field: string, filter: string) => {
  const activeFieldFilters = activeFilters.value[field] || [];
  let newActiveFieldFilters = [] as string[];

  if (activeFieldFilters.includes(filter)) {
    newActiveFieldFilters = [
      ...activeFieldFilters.filter((savedFilter) => savedFilter !== filter),
    ];
  } else {
    newActiveFieldFilters = [...activeFieldFilters, filter];
  }
  activeFilters.value = {
    ...activeFilters.value,
    [field]: newActiveFieldFilters,
  };

  fetchProductData();
};

const fetchProductData = async() => {
  const payload = {
    ...activeFilters.value,
    categories: [...plpDocument.value.ecomCategories],
  };
  const res = await fetchProducts(payload);
  if (res) {
    fetchedProducts.value = res.products || [];
    processFilters(res.data?.filter || []);
  }
};

const batches = computed<Product[][]>(()=> {
  const productBatches = [];
  const productsToBeBatched = [...products.value.filter((p) => !p.isArchived)];
  productBatches.push(productsToBeBatched);
  return productBatches;
});

const hasGridComponents = computed(()=> {
  const gridComp = plpDocument.value.components;
  return gridComp?.filter((p) => p.gridPosition > 0).length > 0 ? true : false;
});

const gridComponents = (gridPos: number) => {
  const gridComp = plpDocument.value.components;
  return gridComp?.filter((p) => p.gridPosition === gridPos);
};

const components = computed<typeof BlockComponents>(()=> {
  return BlockComponents;
});

const openJavascriptLink = (lnk: string) => {
  try {
    eval(lnk);
  } catch (error) {
    console.log(error.message);
  }
};

const clearFilters = () => {
  activeFilters.value = {};
  fetchProductData();
};
const removeFilter = (field: string, filter: string) => {
  const alteredFieldFilters = activeFilters.value[field].filter(
    (activeFilter) => activeFilter !== filter
  );

  activeFilters.value = {
    ...activeFilters.value,
    [field]: alteredFieldFilters,
  };
  fetchProductData();
};

</script>

<style scoped lang="postcss">
</style>
